/**
 * Web Gallery utilities.
 */

import get from 'lodash.get';

import { interpolateString } from '../../../util/context-strings';
import { isAlbum, isAlbumPhotoGroup } from '../../../util/album';
import { PERMISSION_NONE } from '../../../constants/contributors';
import {
  ALBUM_PRICE_MODE,
  ALBUM_SLIDESHOW_STATUS,
} from '../../../constants/albums';

// Users have access to web galleries on the following conditions:
// 1. Unauthenticated + is_private_web_gallery=FALSE
// 2. Phone auth + authenticated + is_private_web_gallery=TRUE
// 3. Email auth + authenticated + user.email_verified + is_private_web_gallery=TRUE
// 4. Has password + (valid_token = TRUE || hasWebGalleryViewAccess())
// 5. Invite only + authenticated + hasWebGalleryViewAccess()

/**
 * Checks if a Web Gallery is invite only.
 *
 * @param {Object} webGallery The Album or AlbumFolder.
 * @returns {boolean}
 */
const webGalleryIsInviteOnly = webGallery => {
  if (!webGallery) {
    return false;
  }

  return (
    webGallery.invite_only_access ||
    webGallery.root_album_folder?.invite_only_access ||
    webGallery.root?.invite_only_access
  );
};

/**
 * Checks if a Web gallery requires authentication to be viewable
 * by the user and if the user has the required authentication to
 * view it.
 *
 * @param {Object} webGallery The web gallery (album folder or album).
 * @param {Boolean} authenticated Whether the caller is authenticated or not.
 * @returns {Boolean} Whether the user needs to authenticate or not before viewing the web gallery.
 */
const requiresAuthentication = (webGallery, authenticated) => {
  return (
    (webGalleryIsInviteOnly(webGallery) || webGallery.is_private_web_gallery) &&
    !authenticated
  );
};

/**
 * Checks if a Web gallery requires email confirmation before it can
 * be viewed by the user in case they have signed up via email.
 *
 * @param {Object} webGallery The web gallery (album folder or album).
 * @param {Object} user The user data of the viewer trying to access the web gallery.
 * @param {Boolean} authenticated Whether the caller is authenticated or not.
 * @returns {Boolean} Whether the user needs to verify their email before viewing the web gallery.
 */
const requiresEmailConfirmation = (webGallery, user, authenticated) => {
  const privateWebGallery = webGallery.is_private_web_gallery;
  const hasUnverifiedEmail = !!user.email_address && !user.email_verified;
  const hasPhone = !!user.phone_number;

  return privateWebGallery && hasUnverifiedEmail && !hasPhone && authenticated;
};

/**
 * Returns whether the Web Gallery (Album or AlbumFolder) is password protected.
 *
 * @param {Object} webGallery The Album or AlbumFolder.
 * @returns {boolean}
 */
const webGalleryHasPassword = webGallery => {
  if (!webGallery) {
    return false;
  }

  const hasOwnPassword = webGallery.has_password;
  const hasRootPassword =
    !!webGallery.root_album_folder?.has_password ||
    !!webGallery.root?.has_password;

  return hasOwnPassword || hasRootPassword;
};

/**
 * Returns the root id of any given Web Gallery (Album or AlbumFolder),
 * ie the Album id for individual Albums and the Root Album Folder id
 * for nested Albums or nested AlbumFolders.
 *
 * @param {Object} webGallery The Album or AlbumFolder.
 * @returns {string}
 */
const getWebGalleryRootId = webGallery => {
  if (!webGallery) {
    return '';
  }

  return (
    webGallery.root_album_folder?.id ??
    webGallery.root?.id ??
    webGallery.id ??
    ''
  );
};

/**
 * Determines whether the user has view access on the Web Gallery
 * based on its ContributorAccess.
 *
 * @param {Object} webGallery The Album or AlbumFolder.
 * @returns {boolean}
 */
const hasWebGalleryViewAccess = webGallery => {
  if (!webGallery) {
    return false;
  }

  const viewAccess = webGallery.contributor_access?.view_access;

  return viewAccess != null && viewAccess !== PERMISSION_NONE;
};

/**
 * Determines whether the user has upload access on the Web Gallery
 * based on its ContributorAccess.
 *
 * @param {Object} webGallery The Album or AlbumFolder.
 * @returns {boolean}
 */
const hasWebGalleryUploadAccess = webGallery => {
  const uploadAccess = webGallery.contributor_access?.upload_access;

  return uploadAccess != null && uploadAccess !== PERMISSION_NONE;
};

/**
 * Determines whether the user has any Contributor access on the Web Gallery
 * based on its ContributorAccess.
 *
 * @param {Object} webGallery The Album or AlbumFolder.
 * @returns {boolean}
 */
const hasWebGalleryContributorAccess = webGallery => {
  return (
    hasWebGalleryViewAccess(webGallery) || hasWebGalleryUploadAccess(webGallery)
  );
};

/**
 * Determines if an album web gallery should show the upsell
 * to PRO Paid product checkout.
 *
 * @param {Object} album - The album.
 * @param {Object} root - The root album folder this album belongs to.
 * @returns {Boolean} - Whether the upsell should show or not.
 */
const shouldShowUpsell = (album, root) => {
  if (!album || !root) {
    return false;
  }

  const isPPG = root.price?.value > 0 || root.is_demo;
  const hasAlbumPricing = root.album_price_mode !== ALBUM_PRICE_MODE.NONE;

  return (isPPG || hasAlbumPricing) && root?.joins_enabled;
};

/**
 * Determines the correct URL for web galleries
 * based on album folder or album passed.
 *
 * @param {Object<Album|AlbumFolder|AlbumPhotoGroup>} entity Album or Album Folder.
 * @returns {String|undefined} The Web Gallery URL.
 */
const getWebGalleryEntityPath = entity => {
  const albumFolderRoute = '/galleries/view/folder/{id}';
  const albumRoute = '/galleries/view/album/{id}';
  const albumPhotoGroupRoute = '/galleries/view/album/{albumId}/subalbums/{id}';

  if (isAlbumPhotoGroup(entity)) {
    const id = get(entity, 'id');
    const albumId = get(entity, 'album.id');
    return (
      (id &&
        albumId &&
        interpolateString(albumPhotoGroupRoute, { id, albumId })) ||
      ''
    );
  }

  if (isAlbum(entity)) {
    const id = get(entity, 'id');
    return (id && interpolateString(albumRoute, { id })) || '';
  }

  const id = get(entity, 'root.id') || get(entity, 'id');

  if (id) {
    return id && interpolateString(albumFolderRoute, { id });
  }

  return '';
};

/**
 * Determines the correct Favorite Photos URL for web galleries
 * based on album folder or album passed.
 *
 * @param {Object<AlbumFolder>} [albumFolder] The album folder.
 * @param {Object<Album>} [album] The album.
 * @returns {String|undefined} The Web Gallery URL.
 */
const getWebGalleryFavoritesPath = (albumFolder, album) => {
  const albumFolderRoute = '/galleries/view/folder/{id}/favorites';
  const albumRoute = '/galleries/view/album/{id}/favorites';

  // If we're on any album folder just return its root album folder
  // as favorites are on the root album folder level in this case
  if (albumFolder) {
    const id = get(albumFolder, 'root.id');
    return id && interpolateString(albumFolderRoute, { id });
  }

  if (album && album.root_album_folder) {
    // If we're in an album check if it's part of an album folder web gallery
    // and return the root
    const id = get(album, 'root_album_folder.id');
    return id && interpolateString(albumFolderRoute, { id });
  }

  if (album) {
    // If we're in an individual album return its own id
    const id = get(album, 'id');
    return id && interpolateString(albumRoute, { id });
  }

  return undefined;
};

/**
 * Given an account object with ALL user album memberships and an album id
 * returns whether the user has a purchased album membership for that
 * album with any of their identities.
 *
 * @param {Object} account The account object with ALL album memberships.
 * @param {string} albumId The album ID.
 * @returns {boolean} Whether the account has purchased the album with any identity.
 */
const hasPurchasedAlbumSubscriptionsOnAlbum = (account, albumId) => {
  if (!account || !albumId) {
    return false;
  }

  const albumMembershipRecords =
    get(account, 'album_memberships.records') || [];
  const albumMembership = albumMembershipRecords.find(
    ({ album }) => album.id === albumId,
  );

  if (!albumMembership) {
    return false;
  }

  const purchasedIdentities = get(
    albumMembership,
    'purchased_identities.records',
    [],
  );
  return (purchasedIdentities || []).length > 0;
};

/**
 * Determines the correct Blog URL for web galleries
 * based on album folder or album passed.
 *
 * @param {Object<Album|AlbumFolder>} entity Album or Album Folder.
 * @returns {String|undefined} The Web Gallery URL.
 */
const getWebGalleryBlogPath = entity => {
  const entityPath = getWebGalleryEntityPath(entity);

  if (entityPath) {
    return `${entityPath}/blog`;
  }

  return '';
};

/**
 * Returns the Waldogram URL path for web galleries.
 *
 * @param {Object} album - The album.
 * @returns {string}
 */
const getWebGalleryWaldogramPath = album => {
  if (!album) {
    return '';
  }

  return `/galleries/albums/${album.id}/postcard`;
};

/**
 * Returns web gallery URL for a blogPostLink.
 *
 * @param {Object<BlogPostLink>} blogPostLink The Blog Post Link Item.
 * @returns {Object} Blog Post Link data.
 * @returns {Object.name} Name of the entity from the blogPostLink.
 * @returns {Object.link} Web gallery URL for the blogPostLink.
 */
const getWebGalleryBlogPostLinkData = blogPostLink => {
  const albumFolderRoute = '/galleries/view/folder/{id}';
  const albumRoute = '/galleries/view/album/{id}/';
  const albumPhotoGroupRoute = '/galleries/view/album/{albumId}/subalbums/{id}';

  const {
    album,
    album_folder: albumFolder,
    album_photo_group: albumPhotoGroup,
  } = blogPostLink;

  if (albumFolder) {
    const id = get(albumFolder, 'id');
    return {
      link: id && interpolateString(albumFolderRoute, { id }),
      name: albumFolder.name,
    };
  }

  if (albumPhotoGroup) {
    const albumId = get(albumPhotoGroup, 'album.id');
    const id = get(albumPhotoGroup, 'id');
    return {
      link: id && interpolateString(albumPhotoGroupRoute, { id, albumId }),
      name: albumPhotoGroup.name,
    };
  }

  if (album) {
    const id = get(album, 'id');
    return {
      link: id && interpolateString(albumRoute, { id }),
      name: album.name,
    };
  }

  return {};
};

/**
 * Determines the correct videos URL for web galleries
 * based on album folder, album or photoGroup passed.
 *
 * @param {Object<Album|AlbumFolder>} entity Album or Album Folder.
 * @returns {String|undefined} The Web Gallery URL.
 */
const getWebGalleryVideosPath = entity => {
  const entityPath = getWebGalleryEntityPath(entity);

  if (entityPath) {
    return `${entityPath}/videos`;
  }

  return '';
};

/**
 * Determines the correct faces URL for web galleries
 * based on album folder, album or photoGroup passed.
 *
 * @param {Object<Album|AlbumFolder>} entity Album or Album Folder.
 * @returns {String|undefined} The Web Gallery URL.
 */
const getWebGalleryFacesPath = entity => {
  const entityPath = getWebGalleryEntityPath(entity);

  if (entityPath) {
    return `${entityPath}/faces`;
  }

  return '';
};

/**
 * Determines the correct slideshow URL for web galleries
 * based off album, slideshow, and owner status.
 *
 * @param {Object} album the album
 * @param {Object} slideshow the slideshow
 * @param {Boolean} isOwner wether or not the user owns the album
 *
 * @returns {string}
 */
const getWebGallerySlideshowPath = (album, slideshow, isOwner) => {
  if (!slideshow) {
    return '';
  }

  if (
    slideshow?.status === ALBUM_SLIDESHOW_STATUS.PUBLISHED ||
    (slideshow?.status === ALBUM_SLIDESHOW_STATUS.DRAFT && isOwner)
  ) {
    return `/albums/${album.id}/slideshow`;
  }

  return '';
};

export {
  requiresAuthentication,
  requiresEmailConfirmation,
  shouldShowUpsell,
  getWebGalleryFavoritesPath,
  hasPurchasedAlbumSubscriptionsOnAlbum,
  getWebGalleryBlogPath,
  getWebGalleryBlogPostLinkData,
  getWebGalleryEntityPath,
  getWebGalleryWaldogramPath,
  getWebGalleryRootId,
  webGalleryHasPassword,
  hasWebGalleryViewAccess,
  hasWebGalleryUploadAccess,
  hasWebGalleryContributorAccess,
  webGalleryIsInviteOnly,
  getWebGalleryVideosPath,
  getWebGallerySlideshowPath,
  getWebGalleryFacesPath,
};
